import { observable, set, toJS, autorun } from "mobx";
import type { Sample, DataField, DataFieldName } from "../../lib/Types";
import _ from "underscore";

function isValidPrePrintedBarcode(barcode) {
  if (!barcode) return false;
  return barcode.match(/^PP([0-9A-Z]+)$/i);
}

class SampleStore {
  constructor(samples: Array<Sample>, groupBy: string, company_id: number) {
    this.groupBy = groupBy;
    this.storageKey = "samples_v2_" + company_id;

    // Load samples from localStorage.
    // Will only happen if previously built sample list was not registered
    if (samples.length === 0) {
      samples = JSON.parse(localStorage.getItem(this.storageKey) || "[]");
      autorun(
        () => {
          localStorage.setItem(this.storageKey, JSON.stringify(toJS(this.samples)));
        },
        { delay: 2000 }
      );
    }

    this.samples = observable(samples.map((s) => ({ ...s, withdrawing: [] })));
  }

  // Can only clone samples when creating new samples
  cloneSample = ({ sample, count, position }): boolean => {
    if (sample.id || count <= 0) return false;
    var newSamples = [];
    var insertAtEndSamples = [];
    this.samples.map((s) => {
      newSamples.push(s);
      if (s === sample) {
        var i;
        for (i = 0; i < count; i++) {
          if (position === "end") insertAtEndSamples.push(toJS(s));
          else newSamples.push(toJS(s));
        }
      }
    });
    insertAtEndSamples.map((s) => newSamples.push(s));
    this.samples.replace(newSamples);
    return true;
  };

  allSamplesAreFromClientList = (): boolean => {
    var fromClientList = true;
    this.samples.map((s) => {
      if (!s.schedule.client_suite_id) {
        fromClientList = false;
      }
    });
    return fromClientList;
  };

  sequencePosition = (sample: Sample): string => {
    if (!sample) return "";
    var index = this.samples.findIndex((s) => s === sample) + 1;
    return index + " / " + this.samples.length;
  };

  clearStorage = () => {
    localStorage.removeItem(this.storageKey);
    this.samples = observable([]);
  };

  removeSample = (sample) => {
    this.samples.remove(sample);
  };

  // do not upload quotables property
  exportSamples = (): Array<Sample> => toJS(this.samples);

  numberOfMissingFields = (): number => {
    var count = 0;
    this.samples.forEach((sample) => {
      sample.data_fields.forEach((dataField) => {
        if (!this.isDataFieldValid(dataField)) {
          count += 1;
        }
      });
      if (sample.protocol) {
        sample.protocol.testing_points.map((testingPoint) => {
          if (!isValidPrePrintedBarcode(testingPoint.pre_printed_barcode)) {
            count += 1;
          }
        });
      }
      if (!sample.protocol) {
        if (!sample.requested_test_date) {
          count += 1;
        }
      }
    });
    return count;
  };

  isDataFieldValid = (dataField: DataField): boolean => {
    if (!dataField.mandatory) return true;
    return dataField.value !== "" && dataField.value !== null && dataField.value !== undefined;
  };

  isSampleValid = (sample: Sample): boolean => {
    return (
      (sample.protocol || sample.requested_test_date) &&
      sample.data_fields.every((dataField) => this.isDataFieldValid(dataField)) &&
      (sample.id || (sample.protocol || sample.quote_item_ids.length > 0)) &&
      this.isProtocolValid(sample)
    );
  };

  // Check that every testing action in the protocol has
  // a pre-printed barcode
  isProtocolValid = (sample: Sample): boolean => {
    if (!sample.protocol) return true;

    var valid = true;
    sample.protocol.testing_points.forEach((testingPoint) => {
      if (!isValidPrePrintedBarcode(testingPoint.pre_printed_barcode)) {
        valid = false;
      }
    });
    return valid;
  };

  invalidSamples = (): Array<Sample> =>
    this.samples.filter((sample) => !this.isSampleValid(sample));

  samplesAreValid = (): boolean => this.invalidSamples().length == 0;

  groupedBySampleInformation = () => {
    const items = {};
    var currentItems = [];
    var currentSampleInformationName = null;

    this.samples.forEach((sample, index) => {
      if (sample.schedule.sample_information_name !== currentSampleInformationName) {
        if (currentSampleInformationName !== null) {
          items[currentSampleInformationName + " ".repeat(index)] = currentItems;
        }
        currentSampleInformationName = sample.schedule.sample_information_name;
        currentItems = [];
      }
      currentItems.push(sample);
      if (sample === this.samples[this.samples.length - 1]) {
        items[currentSampleInformationName + " ".repeat(index)] = currentItems;
      }
    });
    return items;
  };

  copyDownRequestedTestDate = (sourceSample: Sample): void => {
    var found = false;
    this.samplesInDisplayOrder().forEach((sample, index) => {
      if (found) {
        sample.requested_test_date = sourceSample.requested_test_date;
      }
      if (sample === sourceSample) {
        found = true;
      }
    });
  };

  samplesInDisplayOrder = () => {
    var samples = [];
    if (this.groupBy == "sample_information") {
      var groupedSamples = this.groupedBySampleInformation();
      _.keys(groupedSamples).map((groupFields, i) => {
        samples = samples.concat(groupedSamples[groupFields]);
      });
    } else {
      samples = this.samples;
    }
    return samples;
  };

  copyDown = (sourceSample: Sample, dataFieldName: DataFieldName): void => {
    const sourceDataField = sourceSample.data_fields.find(
      (df) => df.data_field_name.id == dataFieldName.id
    );

    if (!sourceDataField) {
      alert("Could not find sample");
      return;
    }

    // copy down
    var found = false;
    this.samplesInDisplayOrder().forEach((sample, index) => {
      if (found) {
        const dataField = sample.data_fields.find(
          (df) => df.data_field_name.id == dataFieldName.id
        );
        if (dataField) {
          if (
            !dataField.type.match(/ammendable_drop_down/) &&
            dataField.type.match(/drop_down/)
          ) {
            if (!sourceDataField.value || dataField.items.includes(sourceDataField.value)) {
              dataField.value = sourceDataField.value;
            }
          } else {
            dataField.value = sourceDataField.value;
          }
        }
      }
      if (sample === sourceSample) {
        found = true;
      }
    });
  };
}

export { SampleStore as default, isValidPrePrintedBarcode };
